<script setup lang="ts" generic="T extends AnyItem">
import type { AnyItem, GlobalCollectionId } from '#layers/data-model';
import type { BadgeProps, ButtonProps } from '#layers/ui';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { ButtonAdd, ButtonDestructive, ButtonEdit, ButtonRemove, ListCard, WithScope } from '#layers/ui';
import { useSkeletonItems } from '~/composables/use-skeleton-items';

export type RelatedItemState = 'default' | 'created' | 'connected' | 'disconnected' | 'updated';

export interface RelatedDisplayItem<TData = AnyItem> {
	id: string;
	data: TData;
	state: RelatedItemState;
}

export interface RelatedItemListCardPermissions<TData = AnyItem> {
	canConnect?: boolean;
	canCreate?: boolean;
	canEdit?: boolean;
	canDisconnect?: boolean;
	// Per-item disconnect gate, evaluated against the row's data item. When set it
	// decides removal per row (e.g. scoped assign grants); when absent the card falls
	// back to the `canDisconnect` boolean.
	canDisconnectItem?: (item: TData) => boolean;
	connectDisabledReason?: string;
	createDisabledReason?: string;
}

interface RelatedItemListCardProps {
	items: RelatedDisplayItem<T>[];
	collectionId: GlobalCollectionId;
	disabled?: boolean;
	disabledNotice?: string;
	readonly?: boolean;
	showEdit?: boolean;
	showCreate?: boolean;
	showAddExisting?: boolean;
	createActionProps?: Partial<ButtonProps>;
	/** Pluralizable i18n key used for the item-count header (e.g. `common.n_files`). */
	countLabel?: string;
	pagination?: {
		page: number;
		pageSize: number;
		totalItems?: number;
		currentItems?: number;
	};
	loading?: boolean;
	permissions?: RelatedItemListCardPermissions<T>;
}

interface RelatedItemListCardEmits {
	'select': [];
	'create': [];
	'edit': [item: T];
	'remove': [item: T];
	'undo': [item: T];
	'update:page': [page: number];
	'update:pageSize': [size: number];
}

const props = withDefaults(defineProps<RelatedItemListCardProps>(), {
	showEdit: true,
	showCreate: true,
	showAddExisting: true,
	countLabel: 'common.n_items',
});

const emit = defineEmits<RelatedItemListCardEmits>();

const permissions = computed(() => ({
	canConnect: props.permissions?.canConnect ?? true,
	canCreate: props.permissions?.canCreate ?? true,
	canEdit: props.permissions?.canEdit ?? true,
	canDisconnect: props.permissions?.canDisconnect ?? true,
	connectDisabledReason: props.permissions?.connectDisabledReason,
	createDisabledReason: props.permissions?.createDisabledReason,
}));

// Disconnect is decided per row: a per-item predicate (scoped grants) takes
// precedence; otherwise the card-level `canDisconnect` boolean applies.
function canDisconnectItem(item: RelatedDisplayItem<T>) {
	if (props.permissions?.canDisconnectItem) return props.permissions.canDisconnectItem(item.data);
	return permissions.value.canDisconnect;
}

function handleRemove(item: RelatedDisplayItem<T>) {
	if (props.disabled || !canDisconnectItem(item)) return;
	emit('remove', item.data);
}

const appConfig = useAppConfig();
const { t } = useI18n();

const listCard = useTemplateRef('listCard');

const showDisabledNotice = computed(() => !!props.disabled && !!props.disabledNotice);
const hasPages = computed(() => {
	if (!props.pagination) return false;
	if (props.pagination.totalItems != null) return props.pagination.totalItems > props.pagination.pageSize;

	const currentItems = props.pagination.currentItems ?? 0;

	// Unknown totals need one full page to keep the next-page control reachable.
	return currentItems >= props.pagination.pageSize;
});

const createButtonProps = computed<Partial<ButtonProps>>(() => ({
	size: 'sm',
	variant: 'solid',
	color: 'neutral',
	label: t('common.create_item'),
	disabled: props.disabled,
	...props.createActionProps,
}));

const listCardHeight = ref<number | null>(null);
const listSkeletonCount = ref(0);
const shouldDisplaySkeleton = ref(false);

const skeletonItems = useSkeletonItems<RelatedDisplayItem<T>>(listSkeletonCount);

onMounted(() => {
	watchEffect(async () => {
		if (listCard.value && props.pagination) {
			if (props.pagination.page === 1) {
				listCardHeight.value = null;
				listSkeletonCount.value = 0;
			}
			else if (props.items.length > listSkeletonCount.value) {
				await nextTick();
				listCardHeight.value = (listCard.value as unknown as { $el: HTMLElement }).$el.clientHeight;
				listSkeletonCount.value = props.items.length;
			}
		}
	});
});

watch(() => props.pagination?.page, () => {
	shouldDisplaySkeleton.value = true;
	listCard.value?.scrollToTop();
});

watch(() => props.loading, (val) => {
	if (!val) {
		shouldDisplaySkeleton.value = false;
	}
});

function handlePageUpdate(page: number) {
	emit('update:page', page);
}

function handlePageSizeUpdate(size: number) {
	emit('update:pageSize', size);
}

const statusBadgePropsByState = computed<Record<RelatedItemState, Partial<BadgeProps> | null>>(() => ({
	default: null,
	created: { color: 'success', icon: appConfig.ui.icons.plus, label: t('common.created') },
	connected: { color: 'info', icon: 'lucide:link', label: t('common.connected') },
	disconnected: { color: 'error', icon: 'lucide:unlink', label: t('common.disconnected') },
	updated: { color: 'info', icon: appConfig.ui.icons.edit, label: t('common.edited') },
}));

defineExpose({
	scrollToTop: () => listCard.value?.scrollToTop(),
	scrollToBottom: () => listCard.value?.scrollToBottom(),
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<ListCard
			ref="listCard"
			:disabled
			:items="(loading && skeletonItems.length > 0) ? skeletonItems : items"
			class="max-h-[max(50vh,500px)]"
			:style="{
				minHeight: listCardHeight ? `${listCardHeight}px` : undefined,
			}"
			:ui="{
				item: {
					base: 'h-13 overflow-hidden',
					content: '-mx-2.5 h-full px-2.5',
				},
			}"
		>
			<template #header>
				<span v-if="!hasPages && !loading" class="text-xs font-medium text-toned">{{ t(countLabel, items.length) }}</span>
			</template>
			<template #header-actions>
				<div v-if="!readonly && (showAddExisting || showCreate)" class="-me-2 flex items-center gap-2">
					<DTooltip
						v-if="showAddExisting"
						:disabled="disabled || permissions.canConnect"
						:text="permissions.connectDisabledReason ?? t('permissions.no_connect_access')"
					>
						<DButton
							data-test-id="add-existing-button"
							size="sm"
							variant="outline"
							color="neutral"
							leading-icon="lucide:link"
							:label="t('common.add_existing_item')"
							:disabled="disabled || !permissions.canConnect"
							:ui="{ label: 'sr-only @sm:not-sr-only' }"
							@click="emit('select')"
						/>
					</DTooltip>
					<DTooltip
						v-if="showCreate"
						:disabled="disabled || permissions.canCreate"
						:text="permissions.createDisabledReason ?? t('permissions.no_create_access')"
					>
						<ButtonAdd
							data-test-id="create-button"
							v-bind="createButtonProps"
							:disabled="disabled || !permissions.canCreate"
							:ui="{ label: 'sr-only @sm:not-sr-only' }"
							@click="emit('create')"
						/>
					</DTooltip>
				</div>
			</template>
			<template #item-content="{ item }">
				<div v-if="loading && shouldDisplaySkeleton" class="animate-[fade-in_600ms_ease-out_400ms_both]">
					<DSkeleton class="h-full w-50 rounded-sm" />
				</div>
				<RenderCollectionDisplayTemplate
					v-else
					class="h-full text-sm"
					:class="{ 'opacity-50': item.state === 'disconnected' }"
					:collection-id
					:item="item.data"
					@click="!loading && !readonly && showEdit && emit('edit', item.data)"
				/>
			</template>
			<template #item-trailing="{ item }">
				<div class="flex items-center gap-2">
					<WithScope v-slot="{ badgeProps }" :define="{ badgeProps: statusBadgePropsByState[item.state ?? 'default'] }">
						<DBadge
							v-if="badgeProps"
							data-test-id="state-badge"
							:data-state="item.state"
							v-bind="badgeProps"
							class="bg-default"
							variant="outline-neutral"
						/>
					</WithScope>
					<div
						v-if="!loading && !readonly"
						class="
							flex w-0 gap-1 opacity-0 transition-all duration-50
							group-hover/list-item:w-auto group-hover/list-item:opacity-100
							group-focus-visible/list-item:w-auto group-focus-visible/list-item:opacity-100
						"
					>
						<ButtonEdit
							v-if="showEdit && item.state !== 'disconnected'"
							size="sm"
							:disabled="!permissions.canEdit || disabled"
							:tooltip="permissions.canEdit ? t('common.edit_item') : t('permissions.no_update_access')"
							@click="emit('edit', item.data)"
						/>
						<DTooltip v-if="item.state === 'created'" :text="t('common.undo_create_item')">
							<ButtonDestructive
								data-test-id="undo-button"
								icon="lucide:undo-2"
								class="p-1.5 text-muted"
								size="sm"
								@click="emit('undo', item.data)"
							/>
						</DTooltip>
						<DTooltip v-else-if="item.state === 'connected'" :text="t('common.undo_select_item')">
							<DButton
								data-test-id="undo-button"
								icon="lucide:undo-2"
								class="text-muted"
								color="neutral"
								variant="ghost"
								size="sm"
								@click="emit('undo', item.data)"
							/>
						</DTooltip>
						<DTooltip v-else-if="item.state === 'disconnected'" :text="t('common.undo_deselect_item')">
							<DButton
								data-test-id="undo-button"
								icon="lucide:undo-2"
								class="text-muted"
								color="neutral"
								variant="ghost"
								size="sm"
								@click="emit('undo', item.data)"
							/>
						</DTooltip>
						<DTooltip v-else :text="canDisconnectItem(item) ? t('common.deselect_item') : t('permissions.no_disconnect_access')">
							<ButtonRemove
								data-test-id="remove-button"
								with-leading-icon
								:trash-bin="false"
								:disabled="disabled || !canDisconnectItem(item)"
								square
								@click="handleRemove(item)"
							/>
						</DTooltip>
					</div>
				</div>
			</template>
			<template #footer>
				<DPaginationBar
					v-if="hasPages && pagination"
					:page="pagination.page"
					:page-size="pagination.pageSize"
					:current-items="pagination.currentItems"
					:total-items="pagination.totalItems"
					:loading
					@update:page="handlePageUpdate"
					@update:page-size="handlePageSizeUpdate"
				/>
			</template>
		</ListCard>
	</DTooltip>
</template>
